import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../api/client';

interface Integration {
  integration_id: string;
  integration_type: string;
  provider: string;
  webhook_url?: string;
  created_at: string;
}

export const AgentIntegrations: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const [items, setItems] = useState<Integration[]>([]);
  const [open, setOpen] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');

  const load = async () => {
    const res = await api.get(`/api/v1/integrations/${agentId}`);
    setItems(res.data.integrations);
  };

  const onCreateWebhook = async () => {
    if (!webhookUrl) return;
    await api.post(`/api/v1/integrations/webhook`, { agent_id: agentId, webhook_url: webhookUrl, events: ['call.completed'], headers: {}, secret_key: '' });
    setOpen(false);
    setWebhookUrl('');
    await load();
  };

  const onDelete = async (integration_id: string) => {
    await api.delete(`/api/v1/integrations/${integration_id}`);
    await load();
  };

  useEffect(() => { load(); }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Integrations</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>Add webhook</Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Webhook URL</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.integration_id} hover>
                <TableCell>{i.integration_type}</TableCell>
                <TableCell>{i.provider}</TableCell>
                <TableCell>{i.webhook_url || '-'}</TableCell>
                <TableCell>{new Date(i.created_at).toLocaleString()}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => onDelete(i.integration_id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add webhook integration</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Webhook URL" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onCreateWebhook} disabled={!webhookUrl}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};